import { normalizeSunoPlaceholdersForStorage } from "utils/suno-merge-tags.util";

import { LEAD_MERGE_TAG_KEYS } from "../../merge-tags/merge-tags-options/lead-contact.utils";
import { PATIENT_NAME_OPTIONS } from "../../merge-tags/merge-tags-options/patient-info/patient-name.utils";
import { PERSONAL_DETAILS_OPTIONS } from "../../merge-tags/merge-tags-options/patient-info/personal-details.utils";
import { PRIMARY_CONTACT_OPTIONS } from "../../merge-tags/merge-tags-options/patient-info/primary-contact.utils";

/**
 * Designer/Unlayer convention: snake_case tokens inside `<!--SUNO:token-->`
 * (or the bare `!--SUNO:token--` form Unlayer inserts while editing).
 *
 * Intentionally name-only: the backend has no `patient_birthdate`/
 * `patient_email`/`patient_phone`/`patient_zip_code`/`patient_last_name`
 * merge tags today (see `src/features/marketing/CLAUDE.md`'s merge-tag
 * registration steps), so there is nothing to map those tokens to yet.
 * Adding them here without backend support would let a clinic pick an
 * unresolvable tag in a real patient-facing template. Tracked as a
 * follow-up once the backend registers those tokens.
 */
const DESIGNER_PATIENT_TO_LEAD_MERGE_TAG_MAP: Record<string, string> = {
  patient_full_name: LEAD_MERGE_TAG_KEYS.LEAD_FULL_NAME,
  patient_first_name: LEAD_MERGE_TAG_KEYS.LEAD_FIRST_NAME
};

/**
 * Word-processor/TinyMCE convention: dotted-Pascal tokens inside `{{Token}}`,
 * all under the `Patient.` prefix. Covers every `Patient.*` field that has a
 * real, already-resolvable Lead equivalent (name, birthdate, primary email/
 * phone/zip). Other `Patient.*` fields (sex, marital status, secondary
 * contact, etc.) have no Lead concept and fall through to the `[token]`
 * escape below.
 */
const WORD_PROCESSOR_PATIENT_TO_LEAD_MERGE_TAG_MAP: Record<string, string> = {
  [PATIENT_NAME_OPTIONS.FullName]: LEAD_MERGE_TAG_KEYS.LEAD_FULL_NAME,
  [PATIENT_NAME_OPTIONS.FirstName]: LEAD_MERGE_TAG_KEYS.LEAD_FIRST_NAME,
  [PATIENT_NAME_OPTIONS.LastName]: LEAD_MERGE_TAG_KEYS.LEAD_LAST_NAME,
  [PERSONAL_DETAILS_OPTIONS.DATE_OF_BIRTH]: LEAD_MERGE_TAG_KEYS.LEAD_BIRTHDATE,
  [PRIMARY_CONTACT_OPTIONS.EMAIL_ADDRESS]: LEAD_MERGE_TAG_KEYS.LEAD_EMAIL,
  [PRIMARY_CONTACT_OPTIONS.PHONE]: LEAD_MERGE_TAG_KEYS.LEAD_PHONE,
  [PRIMARY_CONTACT_OPTIONS.ZIP_CODE]: LEAD_MERGE_TAG_KEYS.LEAD_ZIP_CODE
};

const SUNO_COMMENT_TAG_REGEXP = /<!--SUNO:(.*?)-->/g;
const CURLY_MERGE_TAG_REGEXP = /{{(.*?)}}/g;

/**
 * Translates a main-app (patient-scoped) template's merge tags into their
 * Suno Leads equivalents so it can be inserted on a Leads surface (SN-18785
 * cross-platform template sharing).
 *
 * - Word-processor `{{Patient.*}}` tokens with a real Lead equivalent — name
 *   (full/first/last), birthdate, and primary email/phone/zip — are
 *   rewritten to that equivalent.
 * - Designer/Unlayer `<!--SUNO:patient_*-->` tokens are only mapped for name
 *   (full/first) today; the backend has no birthdate/email/phone/zip/last-
 *   name merge tags on that side yet, so those remain unmapped until the
 *   backend registers them (see `DESIGNER_PATIENT_TO_LEAD_MERGE_TAG_MAP`).
 * - Any other `Patient.*`/designer SUNO token has no Lead concept (or no
 *   backend-registered token) to map to, and is escaped to a visible
 *   `[token]` placeholder instead of silently resolving to blank or leaking
 *   raw SUNO placeholder text.
 * - Non-`Patient.` `{{}}` tokens (Clinic.*, CurrentUser.*, lead_*, …) are left
 *   untouched — they already resolve correctly in a Leads context today.
 */
export const mapPatientMergeTagsForLeads = (text: string): string => {
  let result = normalizeSunoPlaceholdersForStorage(text);

  result = result.replace(SUNO_COMMENT_TAG_REGEXP, (_match, token: string) => {
    const mapped = DESIGNER_PATIENT_TO_LEAD_MERGE_TAG_MAP[token];
    return mapped ? `<!--SUNO:${mapped}-->` : `[${token}]`;
  });

  result = result.replace(CURLY_MERGE_TAG_REGEXP, (match, token: string) => {
    if (!token.startsWith("Patient.")) {
      return match;
    }
    const mapped = WORD_PROCESSOR_PATIENT_TO_LEAD_MERGE_TAG_MAP[token];
    return mapped ? `{{${mapped}}}` : `[${token}]`;
  });

  return result;
};

/**
 * Same translation as `mapPatientMergeTagsForLeads`, applied to an Unlayer
 * design JSON payload (merge tags live as literal text inside the design's
 * rich-text blocks). Mirrors the stringify/replace/parse pattern already used
 * by `resolveMergeTagsInUnlayerDesign`.
 */
export const mapPatientMergeTagsInDesignJson = <T>(design: T): T =>
  JSON.parse(mapPatientMergeTagsForLeads(JSON.stringify(design)));
